
import React from 'react';
import OnboardingLayout from '../../OnboardingLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Check, AlertCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface ProfileCompletionProps {
  formData: any;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const ProfileCompletion: React.FC<ProfileCompletionProps> = ({
  formData,
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  // Calculate profile completion based on formData
  const getCompletionScore = () => {
    let score = 0;
    const totalItems = 5;
    
    if (formData.name) score++;
    if (formData.bio && formData.bio.length > 10) score++;
    if (formData.expertise && formData.expertise.length > 0) score++;
    if (formData.topics && formData.topics.length > 0) score++;
    if (formData.pricing) score++;
    
    return Math.round((score / totalItems) * 100);
  };
  
  const completionScore = getCompletionScore();
  
  const getCompletionText = () => {
    if (completionScore < 40) return "Just starting";
    if (completionScore < 70) return "Good progress";
    if (completionScore < 100) return "Almost there";
    return "All complete!";
  };

  const completionItems = [
    {
      title: "Profile information",
      isComplete: !!formData.name,
      description: "Your name and bio"
    },
    {
      title: "Areas of expertise",
      isComplete: formData.expertise && formData.expertise.length > 0,
      description: "Your specialties and knowledge areas"
    },
    {
      title: "Writing topics",
      isComplete: formData.topics && formData.topics.length > 0,
      description: "Categories for your content"
    },
    {
      title: "Monetization setup",
      isComplete: !!formData.pricing,
      description: "How you'll be compensated"
    },
    {
      title: "Social connections",
      isComplete: false,
      description: "Connect your social accounts (next step)"
    }
  ];

  return (
    <OnboardingLayout
      title="Profile Completion"
      subtitle="Let's make sure your profile has everything it needs"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={onNext}
      onPrevious={onPrevious}
    >
      <div className="max-w-3xl mx-auto">
        <Card className="mb-8">
          <CardContent className="pt-6">
            <h3 className="text-lg font-medium mb-4">Profile Completion</h3>
            
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">{getCompletionText()}</span>
              <span className="text-sm text-muted-foreground">{completionScore}%</span>
            </div>
            
            <Progress value={completionScore} className="h-2 mb-6" />
            
            <div className="space-y-4">
              {completionItems.map((item, index) => (
                <div key={index} className="flex items-start">
                  <div className={`h-5 w-5 rounded-full flex items-center justify-center mt-0.5 mr-3 ${
                    item.isComplete 
                      ? "bg-green-100 text-green-600" 
                      : "bg-muted text-muted-foreground"
                  }`}>
                    {item.isComplete ? <Check className="h-3 w-3" /> : (index + 1)}
                  </div>
                  <div>
                    <div className="font-medium text-sm">{item.title}</div>
                    <div className="text-xs text-muted-foreground">{item.description}</div>
                  </div>
                  <div className="ml-auto">
                    {item.isComplete ? (
                      <span className="text-xs text-green-600">Complete</span>
                    ) : (
                      <span className="text-xs text-amber-600">Pending</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {completionScore < 100 && (
          <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium mb-1">Some items need attention</h4>
              <p className="text-xs text-muted-foreground">
                You can proceed with onboarding, but we recommend completing your profile 
                for the best experience. You can always update these details later in your settings.
              </p>
            </div>
          </div>
        )}
      </div>
    </OnboardingLayout>
  );
};

export default ProfileCompletion;
